import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api';

/**
 * Настройки кэша.
 * Финансовые данные должны быть свежими, поэтому staleTime небольшой,
 * но повторные запросы при переключении вкладок мы гасим.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Нет смысла повторять запрос, если дело в правах или валидации.
        if (error instanceof ApiError && error.status < 500 && error.status !== 429) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Ключи запросов в одном месте — чтобы инвалидация не разъезжалась. */
export const queryKeys = {
  me: ['me'] as const,
  limits: ['me', 'limits'] as const,
  workspaces: ['workspaces'] as const,
  workspace: (id: string) => ['workspace', id] as const,
  members: (workspaceId: string) => ['workspace', workspaceId, 'members'] as const,

  people: (workspaceId: string, filters?: unknown) =>
    ['workspace', workspaceId, 'people', filters ?? {}] as const,
  person: (id: string) => ['person', id] as const,
  teams: (workspaceId: string) => ['workspace', workspaceId, 'teams'] as const,
  contracts: (personId: string) => ['person', personId, 'contracts'] as const,

  wallets: (workspaceId: string) => ['workspace', workspaceId, 'wallets'] as const,
  categories: (workspaceId: string) => ['workspace', workspaceId, 'categories'] as const,
  transactions: (workspaceId: string, filters?: unknown) =>
    ['workspace', workspaceId, 'transactions', filters ?? {}] as const,

  payouts: (workspaceId: string, filters?: unknown) =>
    ['workspace', workspaceId, 'payouts', filters ?? {}] as const,
  payout: (id: string) => ['payout', id] as const,

  recurring: (workspaceId: string) => ['workspace', workspaceId, 'recurring'] as const,
  bills: (workspaceId: string, filters?: unknown) =>
    ['workspace', workspaceId, 'bills', filters ?? {}] as const,
  incomeSources: (workspaceId: string) => ['workspace', workspaceId, 'income-sources'] as const,

  dashboard: (workspaceId: string, period?: unknown) =>
    ['workspace', workspaceId, 'dashboard', period ?? {}] as const,
  upcoming: (workspaceId: string) => ['workspace', workspaceId, 'upcoming'] as const,

  notifications: (filters?: unknown) => ['notifications', filters ?? {}] as const,
  notificationsUnread: ['notifications', 'unread-count'] as const,

  supportTickets: (filters?: unknown) => ['support', 'tickets', filters ?? {}] as const,
  supportTicket: (id: string) => ['support', 'ticket', id] as const,

  adminOverview: ['admin', 'overview'] as const,
  adminUsers: (filters?: unknown) => ['admin', 'users', filters ?? {}] as const,
  adminPlans: ['admin', 'plans'] as const,
  adminWorkspaces: (filters?: unknown) => ['admin', 'workspaces', filters ?? {}] as const,
  adminTickets: (filters?: unknown) => ['admin', 'tickets', filters ?? {}] as const,
  adminAudit: (filters?: unknown) => ['admin', 'audit', filters ?? {}] as const,
  adminSettings: ['admin', 'settings'] as const,
};
